import { ConfirmationDialogRaw } from "@/controls/modals/ConfirmationDialogRaw";
import { HelmetDexteel, MesfModal } from "@dexteel/mesf-core";
import { Button, Typography } from "@mui/material";
import { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import {
	type CalendarEmailFormValues,
	getSelectedRecipients,
	hasNonDefaultRecipients,
} from "./CalendarEmailForm";
import { EmailNotificationFields } from "./EmailNotificationFields";

interface Props {
	open: boolean;
	title?: string;
	message?: string;
	resource: string;
	loading?: boolean;
	onClose: () => void;
	onConfirm: (
		selectedEmails: string[],
		publisherNote: string | undefined,
		addRecipientsToTopic: boolean,
	) => void;
}

const getDefaultValues = (): CalendarEmailFormValues => ({
	notifyByEmail: false,
	selectedDefaultEmails: [],
	customEmails: [],
	publisherNote: "",
});

export const PublishCalendarModal = ({
	open,
	title = "Publish Calendar",
	message = "Are you sure you want to publish...",
	resource,
	loading = false,
	onClose,
	onConfirm,
}: Props) => {
	const methods = useForm<CalendarEmailFormValues>({
		defaultValues: getDefaultValues(),
		mode: "onChange",
	});
	const [recipientsReady, setRecipientsReady] = useState(false);
	const [pendingValues, setPendingValues] =
		useState<CalendarEmailFormValues | null>(null);

	const { formState, handleSubmit } = methods;

	const submit = (
		values: CalendarEmailFormValues,
		addRecipientsToTopic: boolean,
	) => {
		if (!values.notifyByEmail) {
			onConfirm([], undefined, false);
			onClose();
			return;
		}

		const trimmedNote = values.publisherNote.trim();
		onConfirm(
			getSelectedRecipients(values),
			trimmedNote || undefined,
			addRecipientsToTopic,
		);
		onClose();
	};

	const requestSubmit = (values: CalendarEmailFormValues) => {
		if (hasNonDefaultRecipients(values)) {
			setPendingValues(values);
			return;
		}
		submit(values, false);
	};

	return (
		<FormProvider {...methods}>
			{open && <HelmetDexteel title={title} />}
			<MesfModal
				maxWidth="sm"
				fullWidth
				open={open}
				handleClose={onClose}
				title={title}
			>
				<MesfModal.Content dividers>
					<Typography variant="body2" color="textSecondary">
						{message}
					</Typography>
					<EmailNotificationFields
						open={open}
						resource={resource}
						loading={loading}
						getDefaultValues={getDefaultValues}
						onReadyChange={setRecipientsReady}
					/>
				</MesfModal.Content>
				<MesfModal.Actions>
					<Button onClick={onClose} disabled={loading}>
						Cancel
					</Button>
					<Button
						onClick={handleSubmit(requestSubmit)}
						variant="contained"
						color="primary"
						disabled={loading || !recipientsReady || !formState.isValid}
						data-test-id="confirm-publish-emails-button"
					>
						{loading ? "Publishing..." : "Publish"}
					</Button>
				</MesfModal.Actions>
			</MesfModal>
			<ConfirmationDialogRaw
				title="Replace the topic recipients?"
				message="This list differs from the default recipients of the email topic. Do you want to replace the topic list with this one? The email is sent either way."
				open={pendingValues !== null}
				onClose={(value?: string) => {
					const values = pendingValues;
					setPendingValues(null);
					if (values) submit(values, value === "OK");
				}}
			/>
		</FormProvider>
	);
};
